<script setup>
import axios from 'axios';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlDateTimeField from '../xl/XlDateTimeField.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import IconTriangleWarning from '../icons/IconTriangleWarning.vue';
import PolicyLineIcon from '../policyLines/PolicyLineIcon.vue';
import XlField from '../xl/XlField.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import Loading from '../Loading.vue';
import PolicyholderResources from '../policyholders/PolicyholderResources.vue';
import UploadForMemberModal from '../resource-access/UploadForMemberModal.vue';
import useHubMembers from '../../composables/useHubMembers';
import { setPageTitle } from '../../composables/useHubPageTitle';
import { answerOptions } from '../../helpers/policyLines';
import { plural } from '../../helpers/plural';
import { relativeDay } from '../../helpers/relativeDay';
import { isOwner } from '../../helpers/roles';
import { longDate, toLocalDate } from '../../helpers/usDate';
import { useAuthStore } from '../../stores/auth';
import { usePolicyLinesStore } from '../../stores/policyLines';
import { sendPasswordReset } from '../../api/policyholders';

const route = useRoute();
const router = useRouter();
const api = () => window.store?.event_backend_api ?? '/api';

const record = ref(null);
const brokers = ref([]);
const authStore = useAuthStore();
const policyLinesStore = usePolicyLinesStore();
const revoking = ref(false);
const confirmingRevoke = ref(false);
const loading = ref(true);
const missing = ref(false);
const saving = ref(false);

// One card is editable at a time, the way the prototype behaves: opening a
// second would leave two unsaved drafts on screen with no way to tell which
// Done belongs to which.
const editing = ref(null);
const draft = ref({});

// What the draft held the moment the card opened. Done compares against it, so
// closing a card you only looked at neither writes nor claims to have written.
const draftAtOpen = ref('');

// The card asks; the page owns the file input and the modal, the same division
// the members table uses. A page has no global Save for an upload to wait
// behind, so it writes straight through.
const resourcesCard = ref(null);
const policiesCard = ref(null);
const brokerCard = ref(null);
const uploadInput = ref(null);
const upload = ref(null);
const { members: hubMembers, loadHubMembers } = useHubMembers();

const pickFilesToUpload = async () => {
  await loadHubMembers();
  uploadInput.value?.click();
};

const onFilesPicked = (event) => {
  const files = [...(event.target.files || [])];
  event.target.value = '';
  if (!files.length || !resourceMember.value) return;

  upload.value = { member: { ...resourceMember.value, img: 'Default' }, files };
};

const onUploaded = async () => {
  upload.value = null;
  await resourcesCard.value?.reload();
};

const fullName = computed(() => {
  const r = record.value;
  if (!r) return '';
  return `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || r.email || 'Policyholder';
});

const firstName = computed(() => record.value?.first_name || fullName.value);

const policies = computed(() => record.value?.policies?.items ?? []);

// Local users.id: the id space resource access works in.
const resourceMember = computed(() => (record.value
  ? { id: record.value.id, first_name: record.value.first_name, label: fullName.value, email: record.value.email }
  : null));
const policiesUnavailable = computed(() => record.value?.policies?.unavailable === true);
const answers = computed(() => record.value?.registration_answers ?? []);
const anyAnswered = computed(() => answers.value.some((answer) => answer.display));

/** A policy-line answer is chosen from a list; everything else is typed. */
const isChoice = (answer) => ['policy_line', 'custom'].includes(answer.type);
const answerChoices = (answer) => {
  const current = draft.value.answers[answer.name];
  if (answer.type === 'policy_line') return answerOptions(policyLinesStore.lines, current);

  // An option taken off the form since they registered is still their answer.
  const options = answer.options ?? [];
  return current && !options.includes(current) ? [current, ...options] : options;
};
const engagement = computed(() => record.value?.engagement ?? {});

const lineChoices = computed(() => policyLinesStore.options
  .map((line) => ({ value: line.id, label: line.name })));

const viewerIsOwner = computed(() => isOwner(authStore.userData));

const brokerName = (broker) => {
  if (!broker) return '';
  return `${broker.first_name ?? ''} ${broker.last_name ?? ''}`.trim() || broker.email;
};

const asDate = (value) => longDate(value) || '—';

const titleCase = (value) => (value ? `${value[0].toUpperCase()}${value.slice(1)}` : '');

const STATUSES = {
  registered: { label: 'Active', tone: 'success' },
  invited: { label: 'Invited', tone: 'warning' },
  invitation_sent: { label: 'Invited', tone: 'warning' },
  pending: { label: 'Pending', tone: 'warning' },
  access_revoked: { label: 'Access removed', tone: 'error' },
};

const status = computed(() => {
  const value = record.value?.status;
  if (!value) return null;

  return STATUSES[value] ?? { label: titleCase(value.replace(/_/g, ' ')), tone: 'warning' };
});

// The labels the Policyholders list uses for the same two flags. Somebody who
// is neither is a policyholder — "Members" is not a word the product uses.
const roleLabel = computed(() => {
  const roles = [record.value?.is_speaker && 'Speaker', record.value?.is_moderator && 'Admin'].filter(Boolean);
  return roles.length ? roles.join(' · ') : 'Policyholder';
});

const lastActive = computed(() => (record.value?.last_seen_at ? relativeDay(record.value.last_seen_at) : ''));

// "Today" reads as a phrase inside the tag; a date keeps its capital.
const lastActiveTag = computed(() => (lastActive.value && !/\d{4}$/.test(lastActive.value)
  ? lastActive.value.toLowerCase()
  : lastActive.value));

const renewalPhrase = (policy) => {
  const days = policy.days_remaining;
  if (days === null || days === undefined) return '';
  if (days < 0) return `lapsed ${plural(Math.abs(days), 'day')} ago`;
  if (days === 0) return 'renews today';
  return `in ${plural(days, 'day')}`;
};

/** A renewal inside a month is the one the card is red about. */
const isDue = (policy) => policy.renewal_date !== null
  && policy.days_remaining !== null
  && policy.days_remaining <= 30;

// The same question of a date being typed, which carries no day count yet.
const dueSoon = (date) => Boolean(date)
  && (toLocalDate(date) - new Date(new Date().toDateString())) / 86400000 <= 30;

const hasPassed = (date) => Boolean(date) && toLocalDate(date) < new Date(new Date().toDateString());

const asMoney = (amount) => `$${Number(amount).toFixed(2)}`;

/** The year the policy was recorded, which is how long they have been with the agency. */
const heldSince = (policy) => (policy.created_at ? new Date(policy.created_at).getFullYear() : null);

/** A blank premium reads as free, and a nought is a figure somebody chose. */
const hasPremium = (policy) => policy.monthly_premium !== null && policy.monthly_premium !== undefined;

/** "HO-4821-CHI" reads as "HO-••••-CHI": the letters that say what it is, and its last four. */
const maskedNumber = (number) => {
  const value = String(number ?? '');
  if (value.length <= 4) return value;

  const prefix = value.match(/^[A-Za-z]+[-\s]?/)?.[0] ?? '';
  return `${prefix}••••${value.slice(-4)}`;
};

/**
 * "$142", "142" and "142.00" are all the same figure, and "142/mo" carries one.
 * Anything with no figure left in it is nothing, not a nought.
 */
const premiumOf = (typed) => {
  const digits = String(typed ?? '').replace(/[^\d.]/g, '').replace(/\.(?=.*\.)/g, '');

  return digits === '' || Number.isNaN(Number(digits)) ? null : Number(digits);
};

const editingPolicies = computed(() => editing.value === 'policies');

/**
 * The total says how many policies it is a total OF: adding up the premiums on
 * record and calling it the person's monthly outgoing, while another policy has
 * none, would overstate what is known. While the card is open it follows the
 * fields, as the prototype's does.
 */
const premiumTotal = computed(() => {
  const premiums = editingPolicies.value
    ? draft.value.policies.map((row) => premiumOf(row.monthly_premium))
    : policies.value.map((policy) => (hasPremium(policy) ? Number(policy.monthly_premium) : null));
  const known = premiums.filter((premium) => premium !== null);

  if (!known.length) return null;

  const total = known.reduce((sum, premium) => sum + premium, 0);
  const noun = premiums.length === 1 ? 'policy has' : 'policies have';

  return {
    amount: `${asMoney(total)} a month`,
    count: `${known.length} of ${premiums.length} ${noun} a premium recorded`,
  };
});

/**
 * The server names each policy's line from the SSO type it is filed under. A
 * type no line claims falls back to the type itself.
 */
const policyTypeLabel = (policy) => policy.line?.name || titleCase(policy.type);

// No line at all: a neutral shield rather than another line's colour.
const iconStyle = (policy) => {
  const color = policy.line?.color || '#867D8D';
  return { background: `${color}16`, color };
};

// The picker offers only published lines; a policy on an archived line, or on
// a type no line claims, is still filed under it, so Edit names it.
const pickPlaceholder = (row) => {
  const policy = policies.value.find((held) => held.id === row.id);
  return (policy && policyTypeLabel(policy)) || 'Select';
};

const lineNameFor = (id) => lineChoices.value.find((line) => String(line.value) === String(id))?.label ?? '';

const coverNames = computed(() => (editingPolicies.value
  ? draft.value.policies.map((row) => lineNameFor(row.policy_line_id))
  : policies.value.map(policyTypeLabel)).filter(Boolean));

const coverPhrase = computed(() => {
  const names = [...new Set(coverNames.value.map((name) => name.toLowerCase()))];

  return names.length > 1
    ? `gets a separate one for ${names.slice(0, -1).join(', ')} and ${names.at(-1)} cover`
    : `gets one, naming ${names[0]} cover`;
});

const hasPolicyRows = computed(() => (editingPolicies.value ? draft.value.policies.length : policies.value.length) > 0);

/**
 * The prototype's warning over the columns: what this record is missing that
 * stops the agency looking after them. Nothing is claimed about policies the
 * gateway could not return.
 */
const gaps = computed(() => {
  const r = record.value;
  if (!r) return [];

  const known = !policiesUnavailable.value;

  return [
    !r.broker && 'no broker',
    known && !policies.value.length && 'no policies on record',
    known && policies.value.some((policy) => !policy.renewal_date) && 'a policy with no renewal date',
  ].filter(Boolean);
});

const gapsDetail = computed(() => (policies.value.length && policies.value.every((policy) => policy.renewal_date)
  ? 'Nobody is watching their renewal.'
  : 'A policy needs a renewal date before any reminder can be timed from it.'));

const load = async () => {
  // The skeleton stands in for the whole page, so only the first read shows it:
  // a re-read after a save must leave the fields being edited where they are.
  loading.value = !record.value;
  missing.value = false;

  try {
    const { data } = await axios.get(`${api()}/policyholders/${route.params.id}`);
    record.value = data.data ?? data;
    setPageTitle(fullName.value);
  } catch (error) {
    // The endpoint answers 404 for a record outside the viewer's scope as well
    // as one that does not exist, so this page must not distinguish them either.
    if (error?.response?.status === 404) missing.value = true;
    else window.loadNotification?.({ message: 'Could not load this policyholder', error: true });
  } finally {
    loading.value = false;
  }
};

const loadPolicyLines = () => policyLinesStore.load();

const loadBrokers = async () => {
  if (!record.value?.can_assign_broker) return;

  try {
    const { data } = await axios.get(`${api()}/brokers`);
    brokers.value = data.data ?? data ?? [];
  } catch {
    brokers.value = [];
  }
};

const bookOf = (broker) => {
  const size = broker.book_size ?? 0;
  return `${size} ${size === 1 ? 'policyholder' : 'policyholders'}`;
};

const brokerChoices = computed(() => [
  { value: '', label: '— Nobody yet' },
  ...brokers.value.map((broker) => ({ value: String(broker.id), label: `${brokerName(broker)} · ${bookOf(broker)}` })),
]);

const draftBroker = computed(() => brokers.value.find((broker) => String(broker.id) === draft.value.broker_id) ?? null);

/** A policy as the edit form holds it: strings, because that is what inputs are. */
const asRow = (policy) => ({
  id: policy.id,
  policy_line_id: policy.line?.id ?? null,
  policy_number: policy.policy_number ?? '',
  description: policy.description ?? '',
  renewal_date: policy.renewal_date ?? '',
  monthly_premium: policy.monthly_premium === null || policy.monthly_premium === undefined
    ? ''
    : Number(policy.monthly_premium).toFixed(2),
});

const startEdit = (card) => {
  const r = record.value;
  editing.value = card;
  draft.value = {
    first_name: r.first_name ?? '',
    last_name: r.last_name ?? '',
    phone: r.phone ?? '',
    address: r.address ?? '',
    // A select holds strings; '' is nobody.
    broker_id: r.broker?.id ? String(r.broker.id) : '',
    answers: Object.fromEntries((r.registration_answers ?? []).map((a) => [a.name, a.value ?? ''])),
    policies: card === 'policies' ? policies.value.map(asRow) : [],
    removed: [],
  };
  draftAtOpen.value = JSON.stringify(draft.value);
};

/**
 * A policy is booked against a line, so a hub with no line to book one against
 * cannot add a policy at all. The button says so rather than posting one the
 * gateway would refuse.
 */
const canAddPolicy = computed(() => lineChoices.value.length > 0);

const addPolicy = () => {
  if (!canAddPolicy.value) return;
  if (editing.value !== 'policies') startEdit('policies');
  draft.value.policies.push({
    id: null,
    policy_line_id: lineChoices.value[0].value,
    policy_number: '',
    description: '',
    renewal_date: '',
    monthly_premium: '',
  });
};

const dropPolicy = (index) => {
  const [row] = draft.value.policies.splice(index, 1);
  if (row.id) draft.value.removed.push(row.id);
};

// Tidied on the way out of the field as well as on the way to the server: a
// figure typed and left without blurring is still what they meant.
const tidyPremium = (row) => {
  const premium = premiumOf(row.monthly_premium);
  row.monthly_premium = premium === null ? '' : premium.toFixed(2);
};

const fixTarget = computed(() => {
  if (!record.value?.broker && policies.value.length) return record.value?.can_assign_broker ? 'broker' : null;
  return canAddPolicy.value || policies.value.length ? 'policies' : null;
});

const fixIt = async () => {
  if (fixTarget.value === 'broker') {
    startEdit('broker');
  } else if (policies.value.length) {
    startEdit('policies');
  } else {
    addPolicy();
  }

  await nextTick();
  const card = fixTarget.value === 'broker' ? brokerCard.value : policiesCard.value;
  card?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};

const cancelEdit = () => {
  editing.value = null;
  draft.value = {};
};

const saveIdentity = async () => {
  await axios.patch(`${api()}/policyholders/${record.value.id}`, {
    first_name: draft.value.first_name,
    last_name: draft.value.last_name,
    phone: draft.value.phone,
    address: draft.value.address,
  });
};

const saveBroker = async () => {
  await axios.put(`${api()}/users/${record.value.id}/broker`, {
    broker_id: draft.value.broker_id === '' ? null : Number(draft.value.broker_id),
  });
};

const saveAnswers = async () => {
  const payload = { id: record.value.id, sso_user_id: record.value.sso_user_id };
  for (const [name, value] of Object.entries(draft.value.answers)) payload[`reg_${name}`] = value;

  await axios.post(`${api()}/user-settings-update-all/attendee`, payload);
};

const asPayload = (row) => ({
  // A native select hands back a string, and the id is an id.
  policy_line_id: Number(row.policy_line_id),
  policy_number: row.policy_number.trim() || null,
  renewal_date: row.renewal_date || null,
  monthly_premium: premiumOf(row.monthly_premium),
  // Blank is none, not an empty string a policyholder would see as nothing.
  description: row.description.trim() || null,
});

const atOpen = (row) => JSON.parse(draftAtOpen.value).policies.find((was) => was.id === row.id);

const untouched = (row) => Boolean(row.id) && JSON.stringify(atOpen(row)) === JSON.stringify(row);

// The line goes only when it was changed: the one a policy is filed under may
// be a line the server would refuse to book, and must survive a premium edit.
const changesTo = (row) => {
  const { policy_line_id: lineId, ...rest } = asPayload(row);
  return String(atOpen(row)?.policy_line_id) === String(row.policy_line_id) ? rest : { policy_line_id: lineId, ...rest };
};

// One write at a time. A policy that is stored takes the id it was given, so a
// refusal further down the card leaves a retry patching what already exists
// rather than booking it a second time under a number SSO would then refuse.
const savePolicies = async () => {
  const url = `${api()}/policyholders/${record.value.id}/policies`;

  try {
    while (draft.value.removed.length) {
      await axios.delete(`${url}/${draft.value.removed[0]}`);
      draft.value.removed.shift();
    }

    for (const row of draft.value.policies) {
      if (untouched(row)) continue;

      if (row.id) {
        await axios.patch(`${url}/${row.id}`, changesTo(row));
      } else {
        const { data } = await axios.post(url, asPayload(row));
        row.id = data?.data?.id ?? null;
      }
    }
  } catch (error) {
    await load();
    throw error;
  } finally {
    // A line is keyed by its first booking; the segment builder reads keys from this copy.
    policyLinesStore.load({ force: true });
  }
};

const SAVERS = {
  who: saveIdentity,
  broker: saveBroker,
  answers: saveAnswers,
  policies: savePolicies,
};

const done = async () => {
  const save = SAVERS[editing.value];
  if (!save) return cancelEdit();

  // Nothing was touched, so there is nothing to save and nothing to announce.
  // "Policyholder updated" over an unchanged record is a lie the reader has no
  // way to check.
  if (JSON.stringify(draft.value) === draftAtOpen.value) return cancelEdit();

  saving.value = true;

  try {
    await save();
    await load();
    cancelEdit();
    window.loadNotification?.({ message: 'Policyholder updated' });
  } catch (error) {
    const message = error?.response?.data?.message;
    window.loadNotification?.({ message: message || 'Could not save that change', error: true });
  } finally {
    saving.value = false;
  }
};

const designationSaving = ref(false);
const designationError = ref('');
const ticked = ref(false);

// Saved on the tick, not behind a Done. The tick only sends an invitation, so
// it never stays ticked: they are a policyholder until they accept.
const setDesignation = async (invite) => {
  ticked.value = invite;
  designationSaving.value = true;
  designationError.value = '';

  try {
    await axios.put(`${api()}/users/${record.value.id}/broker-designation`, { is_broker: invite });
    await load();
  } catch (error) {
    designationError.value = error?.response?.data?.message || 'That could not be saved.';
  } finally {
    ticked.value = false;
    designationSaving.value = false;
  }
};

const sendReset = async () => {
  try {
    await sendPasswordReset(record.value.email);
    window.loadNotification?.({ message: 'Password reset link sent' });
  } catch (error) {
    const message = error?.response?.data?.message;
    window.loadNotification?.({ message: message || 'Could not send the reset link', error: true });
  }
};

const revokeAccess = async () => {
  revoking.value = true;

  try {
    await axios.post(`${api()}/policyholders/${record.value.id}/revoke`);
    window.loadNotification?.({ message: 'Access removed' });
    router.push('/policyholders');
  } catch (error) {
    const message = error?.response?.data?.message;
    window.loadNotification?.({ message: message || 'Could not remove their access', error: true });
  } finally {
    revoking.value = false;
  }
};

const openMessages = () => router.push({ path: '/messages', query: { thread: record.value.id } });

const outOf = (part, whole) => (Number.isFinite(whole) && whole > 0 ? `${part ?? 0} of ${whole}` : '');

onMounted(async () => {
  await load();
  await Promise.all([loadBrokers(), loadPolicyLines()]);
});

onUnmounted(() => setPageTitle(null));
</script>

<template>
  <div class="record" data-xl-gen="northstar">
    <Loading v-if="loading" variant="detail" />

    <XlCard v-else-if="missing" padded class="record__missing">
      <h2>We can't find that policyholder</h2>
      <p>They may have been removed, or they belong to another broker's book.</p>
      <XlButton variant="ghost" @click="router.push('/policyholders')">All policyholders</XlButton>
    </XlCard>

    <template v-else-if="record">
      <XlButton variant="ghost" size="sm" class="record__back" @click="router.push('/policyholders')">
        <template #icon>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m14.2 18-6-6 6-6" />
          </svg>
        </template>
        All policyholders
      </XlButton>

      <XlCard padded class="record__hero">
        <div class="record__hero-row">
          <XlAvatar :name="fullName" :src="record.photo || ''" :color="record.avatar_color || ''" :size="66" style="font-size: 21px" />

          <div class="record__identity">
            <h1>{{ fullName }}</h1>
            <p class="record__contact">
              {{ record.email }}<template v-if="record.phone"> · {{ record.phone }}</template>
            </p>
            <div class="record__tags" data-testid="record-tags">
              <XlTag v-if="status" :tone="status.tone">{{ status.label }}</XlTag>
              <XlTag>{{ roleLabel }}</XlTag>
              <XlTag>Registered {{ asDate(record.registered_at || record.created_at) }}</XlTag>
              <XlTooltip text="The last time they opened the portal. A long gap is the early warning before somebody leaves.">
                <XlTag>Last active {{ lastActiveTag || 'never' }}</XlTag>
              </XlTooltip>
            </div>
          </div>

          <div class="record__hero-actions">
            <XlButton variant="ghost" data-testid="record-message" @click="openMessages">
              <template #icon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
                  <path d="m4.6 8 7.4 5 7.4-5" />
                </svg>
              </template>
              Message
            </XlButton>
            <XlButton
              :variant="editing === 'who' ? 'filled' : 'ghost'"
              :loading="saving && editing === 'who'"
              @click="editing === 'who' ? done() : startEdit('who')"
            >{{ editing === 'who' ? 'Done' : 'Edit details' }}</XlButton>
          </div>
        </div>

        <div v-if="editing === 'who'" class="record__edit">
          <div class="record__pair">
            <XlField v-model="draft.first_name" label="First name" name="first_name" />
            <XlField v-model="draft.last_name" label="Last name" name="last_name" />
          </div>
          <div class="record__pair">
            <XlField :model-value="record.email" label="Email" name="email" disabled hint="Email is managed by sign-in and cannot be changed here." />
            <XlField v-model="draft.phone" type="tel" label="Phone" name="phone" />
          </div>
          <XlField v-model="draft.address" label="Address" name="address" maxlength="255" />
        </div>
      </XlCard>

      <XlCard v-if="gaps.length" padded tone="warning" class="record__gaps" data-testid="record-gaps">
        <span class="record__gaps-icon">
          <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 4.4 3.6 19.6h16.8z" />
            <path d="M12 10v3.8M12 16.8h.01" />
          </svg>
        </span>
        <p class="record__gaps-text">
          <b>This record has {{ gaps.join(', ') }}.</b>
          {{ gapsDetail }}
        </p>
        <XlButton v-if="fixTarget" data-testid="record-fix-it" @click="fixIt">Fix it</XlButton>
      </XlCard>

      <div class="record__columns">
        <div class="record__col">
          <XlCard ref="policiesCard" padded>
            <div class="record__card-head">
              <h2>Policies</h2>
              <span class="record__card-actions">
                <XlButton
                  variant="ghost"
                  size="sm"
                  data-testid="add-policy"
                  :disabled="!canAddPolicy"
                  :title="canAddPolicy ? null : 'This hub has no policy line to book a policy against'"
                  @click="addPolicy"
                >
                  <template #icon>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M12 6.2v11.6M6.2 12h11.6" />
                    </svg>
                  </template>
                  Add a policy
                </XlButton>
                <XlButton
                  v-if="policies.length || editing === 'policies'"
                  :variant="editing === 'policies' ? 'filled' : 'ghost'"
                  size="sm"
                  data-testid="policies-done"
                  :loading="saving && editing === 'policies'"
                  @click="editing === 'policies' ? done() : startEdit('policies')"
                >{{ editing === 'policies' ? 'Done' : 'Edit' }}</XlButton>
              </span>
            </div>

            <p v-if="policiesUnavailable" class="record__warn">
              We couldn't reach the policy record just now, so this may be incomplete.
            </p>

            <template v-else-if="editing === 'policies'">
              <div
                v-for="(row, index) in draft.policies"
                :key="row.id ?? `new-${index}`"
                class="record__policy-draft"
                :class="{ 'record__policy--due': dueSoon(row.renewal_date) }"
                data-testid="policy-draft"
              >
                <div class="record__policy-fields">
                  <XlDropdown
                    v-model="row.policy_line_id"
                    class="record__line-pick"
                    label="Policy line"
                    tip="One kind of cover you sell. What each one covers is written once under Policy lines."
                    :options="lineChoices"
                    :placeholder="pickPlaceholder(row)"
                  />
                  <XlField
                    v-model="row.policy_number"
                    label="Policy number"
                    :name="`policy_number_${index}`"
                    placeholder="HO-0000"
                  />
                  <XlField
                    v-model="row.monthly_premium"
                    label="Monthly premium"
                    tip="What this policy costs them a month. Somebody with home and auto pays two."
                    :name="`monthly_premium_${index}`"
                    class="record__premium"
                    inputmode="decimal"
                    placeholder="0.00"
                    @blur="tidyPremium(row)"
                  />
                  <XlDateTimeField
                    v-model="row.renewal_date"
                    label="Renews on"
                    tip="When this particular policy runs out. Reminder sequences count backwards from here."
                    :data-testid="`renewal_date_${index}`"
                  />
                  <button type="button" class="record__policy-remove" aria-label="Remove this policy" @click="dropPolicy(index)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true">
                      <path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8" />
                    </svg>
                  </button>
                </div>
                <div class="record__policy-description">
                  <XlField
                    v-model="row.description"
                    label="Description"
                    hint="What this policy is for this person — the address, the car. They see it in their portal."
                    :name="`description_${index}`"
                    placeholder="e.g. 1420 N Lincoln Ave"
                    maxlength="120"
                  />
                </div>
                <p v-if="hasPassed(row.renewal_date)" class="record__policy-hint record__policy-hint--error">
                  <IconTriangleWarning color="currentColor" />
                  This renewal date has already passed.
                </p>
                <p v-if="!row.renewal_date" class="record__policy-hint">
                  <IconTriangleWarning color="currentColor" />
                  No renewal date — this policy is in no reminder sequence.
                </p>
              </div>

              <p v-if="!draft.policies.length" class="record__muted">
                Nothing on record. Add a policy to start one.
              </p>
            </template>

            <template v-else-if="policies.length">
              <div
                v-for="policy in policies"
                :key="policy.id"
                class="record__policy"
                :class="{ 'record__policy--due': isDue(policy) }"
                data-testid="policy"
              >
                <span class="record__policy-icon" :style="iconStyle(policy)" data-testid="policy-icon">
                  <PolicyLineIcon :name="policy.line?.icon" :size="20" />
                </span>

                <div class="record__policy-what">
                  <b>{{ policyTypeLabel(policy) || 'Policy' }}</b>
                  <div class="record__muted" data-testid="policy-number">
                    {{ policy.policy_number ? maskedNumber(policy.policy_number) : 'no policy number' }}<template v-if="heldSince(policy)"> · with you since {{ heldSince(policy) }}</template>
                  </div>
                  <div v-if="policy.description" class="record__muted" data-testid="policy-description">{{ policy.description }}</div>
                  <div class="record__policy-premium">
                    <template v-if="hasPremium(policy)"><b>{{ asMoney(policy.monthly_premium) }}</b><span class="record__policy-per"> a month</span></template>
                    <XlTag v-else tone="warning">no premium recorded</XlTag>
                  </div>
                </div>

                <!--
                  One phrase, not a date over a day count: the card is narrow
                  enough that the block wraps onto its own line, and two
                  separately aligned lines read as two unrelated facts.
                -->
                <div v-if="policy.renewal_date" class="record__policy-when">
                  <b :class="{ 'record__policy-when--due': isDue(policy) }">Renews {{ longDate(policy.renewal_date) }}</b>
                  <span class="record__muted">· {{ renewalPhrase(policy) }}</span>
                </div>
                <XlTag v-else tone="error">
                  <IconTriangleWarning color="currentColor" />
                  no renewal date
                </XlTag>
              </div>
            </template>

            <p v-else class="record__muted record__none" data-testid="no-policies">
              No policies on record. Until one is added with a renewal date, {{ firstName }}
              sits in no segment and receives no reminders.
              <span v-if="!canAddPolicy" data-testid="no-policy-types">
                This hub has no policy line to book one against yet; the owner adds one on Policy lines.
              </span>
            </p>

            <template v-if="!policiesUnavailable && hasPolicyRows">
              <div v-if="premiumTotal" class="record__policy-total" data-testid="premium-total">
                <b>{{ premiumTotal.amount }}</b>
                <span class="record__muted">{{ premiumTotal.count }}</span>
              </div>

              <p class="record__footnote" data-testid="policies-footnote">
                Reminders count backwards from <b>each policy's own renewal date</b><template v-if="coverNames.length">, so
                {{ firstName }} {{ coverPhrase }}</template>.
                <template v-if="viewerIsOwner">
                  The lines themselves are set under
                  <router-link to="/policy-lines">Policy lines</router-link>.
                </template>
              </p>
            </template>
          </XlCard>

          <XlCard padded data-testid="record-answers">
            <div class="record__card-head">
              <h2>What they told us when they registered</h2>
              <XlButton
                v-if="answers.length"
                :variant="editing === 'answers' ? 'filled' : 'ghost'"
                size="sm"
                :loading="saving && editing === 'answers'"
                @click="editing === 'answers' ? done() : startEdit('answers')"
              >{{ editing === 'answers' ? 'Done' : 'Edit' }}</XlButton>
            </div>

            <div v-if="editing === 'answers'" class="record__answers-edit">
              <!--
                A policy-line answer is the line's id, so it is picked from the
                hub's lines rather than typed: a free-text box here would let
                somebody save a number that resolves to nothing, or a name that
                stops matching the moment the line is renamed.
              -->
              <template v-for="answer in answers" :key="answer.name">
                <XlDropdown
                  v-if="isChoice(answer)"
                  v-model="draft.answers[answer.name]"
                  class="record__answer-pick"
                  :label="answer.title"
                  :options="answerChoices(answer)"
                  placeholder="Not answered"
                />
                <XlField
                  v-else
                  v-model="draft.answers[answer.name]"
                  :label="answer.title"
                  :name="`reg_${answer.name}`"
                  :type="answer.type === 'textarea' ? 'textarea' : 'text'"
                  maxlength="300"
                  :counted="false"
                  placeholder="Not answered"
                />
              </template>
            </div>

            <div v-else-if="anyAnswered" class="record__answers">
              <div v-for="answer in answers" :key="answer.name" class="record__answer" data-testid="record-answer">
                <div class="record__answer-q">{{ answer.title }}</div>
                <div class="record__answer-a">{{ answer.display || '—' }}</div>
              </div>
            </div>

            <p v-else class="record__muted record__none" data-testid="record-no-answers">
              {{ answers.length
                ? "They didn't answer the optional questions."
                : 'The registration form asks nothing beyond their details.' }}
            </p>
          </XlCard>

          <PolicyholderResources
            v-if="resourceMember"
            ref="resourcesCard"
            :member="resourceMember"
            :can-manage="record.can_manage_resources === true"
            @upload-requested="pickFilesToUpload"
          />
        </div>

        <div class="record__col">
          <XlCard ref="brokerCard" padded>
            <div class="record__card-head">
              <h2>
                <XlTooltip text="The person at your agency who looks after them. They see this name as their contact in the portal." underline>Their broker</XlTooltip>
              </h2>
              <XlButton
                v-if="record.can_assign_broker"
                :variant="editing === 'broker' ? 'filled' : 'ghost'"
                size="sm"
                :loading="saving && editing === 'broker'"
                @click="editing === 'broker' ? done() : startEdit('broker')"
              >{{ editing === 'broker' ? 'Done' : 'Edit' }}</XlButton>
            </div>

            <div v-if="editing === 'broker'" class="record__broker-edit">
              <XlDropdown
                v-model="draft.broker_id"
                class="record__broker-pick"
                aria-label="Their broker"
                :options="brokerChoices"
              />
              <p v-if="draftBroker" class="record__broker-note">
                {{ firstName }} sees {{ draftBroker.first_name || brokerName(draftBroker) }} as their contact in the
                portal. Change it and the chat history moves across too.
              </p>
              <p v-else class="record__broker-note record__broker-note--alert">
                <IconTriangleWarning color="currentColor" />
                Until you pick somebody, {{ firstName }} sees no contact and nobody watches their renewal.
              </p>
            </div>

            <div v-else-if="record.broker" class="record__broker">
              <XlAvatar :name="brokerName(record.broker)" :src="record.broker.photo || ''" :size="42" />
              <span class="record__broker-who">
                <b>{{ brokerName(record.broker) }}</b>
                <span v-if="record.broker.book_size !== undefined" class="record__muted">
                  {{ plural(record.broker.book_size, 'policyholder') }} in their book
                </span>
              </span>
            </div>

            <!--
              An alert, not a caption. The banner over the list says the same
              thing in red, so a muted line here had the record contradicting
              the page that sent you to it.
            -->
            <XlCard v-else tone="error" class="record__nobody" data-testid="record-no-broker">
              <IconTriangleWarning color="currentColor" />
              <p>
                <b>Nobody owns this person.</b>
                They see no contact in the portal, and their renewal has nobody watching it.
              </p>
            </XlCard>

            <p v-if="!record.can_assign_broker" class="record__broker-note">
              Only the agency owner can move a policyholder to a different broker.
            </p>

            <div v-if="record.can_assign_broker && viewerIsOwner && editing !== 'broker'" class="record__designation" data-testid="broker-designation">
              <p v-if="record.is_broker" class="record__broker-note">
                {{ firstName }} is a broker. Brokers are managed in Team & Users.
              </p>
              <template v-else-if="record.broker_invited">
                <p class="record__broker-note" data-testid="broker-invitation-pending">
                  Invitation sent — they become a broker when they accept.
                </p>
                <XlButton variant="ghost" size="sm" :loading="designationSaving" @click="setDesignation(false)">Cancel invitation</XlButton>
              </template>
              <label v-else>
                <input type="checkbox" :checked="ticked" :disabled="designationSaving" @change="setDesignation(true)" />
                <span>This person is a broker. Ticking emails them an invitation to join your team; they become one when they accept.</span>
              </label>
              <p v-if="designationError" class="record__designation-error" role="alert">{{ designationError }}</p>
            </div>
          </XlCard>

          <XlCard padded>
            <div class="record__card-head record__card-head--plain"><h2>Account</h2></div>

            <dl class="record__facts" data-testid="record-account">
              <dt>Role</dt>
              <dd>{{ roleLabel }}</dd>
              <dt>Status</dt>
              <dd>{{ status ? status.label : '—' }}</dd>
              <dt>Registered</dt>
              <dd>{{ asDate(record.registered_at || record.created_at) }}</dd>
              <dt>Last active</dt>
              <dd>{{ lastActive || 'Never signed in' }}</dd>
              <dt>Phone</dt>
              <dd :class="{ 'record__facts-missing': !record.phone }">{{ record.phone || '— not given' }}</dd>
              <dt>Address</dt>
              <dd data-testid="record-address">{{ record.address || '—' }}</dd>
              <dt>Customer reference</dt>
              <dd data-testid="record-customer-reference">{{ record.customer_reference || '—' }}</dd>
            </dl>

            <XlButton variant="ghost" class="record__wide" @click="sendReset">Send a password reset link</XlButton>

            <template v-if="record.can_remove_access">
              <XlButton
                v-if="!confirmingRevoke"
                variant="ghost-danger"
                class="record__wide record__revoke"
                @click="confirmingRevoke = true"
              >Remove their access</XlButton>

              <div v-else class="record__confirm">
                <p>They lose the portal immediately, and their broker keeps nothing to message.</p>
                <div class="record__confirm-actions">
                  <XlButton variant="ghost" size="sm" @click="confirmingRevoke = false">Keep their access</XlButton>
                  <XlButton variant="danger" size="sm" :loading="revoking" @click="revokeAccess">Remove it</XlButton>
                </div>
              </div>
            </template>
          </XlCard>

          <XlCard padded>
            <div class="record__card-head record__card-head--plain"><h2>Engagement</h2></div>

            <dl class="record__facts record__facts--last" data-testid="record-engagement">
              <dt>Sessions attended</dt>
              <dd>{{ outOf(engagement.sessions_attended, engagement.sessions_held) || 'No sessions yet' }}</dd>
              <dt>Messages opened</dt>
              <dd>{{ outOf(engagement.messages_opened, engagement.messages_sent) || 'None sent yet' }}</dd>
              <dt>Last logged in</dt>
              <dd>{{ lastActive || 'Never' }}</dd>
              <dt>Waiting on a reply</dt>
              <dd>{{ engagement.waiting_on_reply ? 'Yes' : 'No' }}</dd>
            </dl>
          </XlCard>
        </div>
      </div>

      <input
        ref="uploadInput"
        type="file"
        aria-label="Files to upload"
        accept="application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/jpeg,image/png,image/gif,text/plain,application/pdf"
        multiple
        hidden
        @change="onFilesPicked"
      />

      <UploadForMemberModal
        v-if="upload"
        :member="upload.member"
        :files="upload.files"
        :members="hubMembers"
        @cancel="upload = null"
        @uploaded="onUploaded"
      />
    </template>
  </div>
</template>

<style scoped>
.record, .record * { box-sizing: border-box; }

/* Same gutter as the list this page opens from (see PolicyLines.vue). */
.record {
  container-type: inline-size;
  padding: var(--setup-page-padding) var(--setup-page-padding) 60px;
  font-family: var(--xl-font-family);
  color: var(--xl-color-primary-text);
}

.record__back { margin-bottom: 18px; }

.record__missing { max-width: 460px; }
.record__missing h2 { margin: 0 0 6px; font-size: var(--xl-font-size-lg); }
.record__missing p { margin: 0 0 16px; color: var(--xl-color-outline); }

.record__hero-row {
  display: flex;
  gap: 18px;
  align-items: center;
  flex-wrap: wrap;
}

.record__identity { flex: 1; min-width: 200px; }
.record__identity h1 { margin: 0 0 4px; font-size: 26px; font-weight: var(--xl-font-weight-bold); letter-spacing: -0.03em; }

.record__contact { margin: 0; font-size: 14px; color: var(--xl-color-outline); }

.record__tags { display: flex; gap: 7px; margin-top: 10px; flex-wrap: wrap; }

.record__hero-actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* The prototype puts 18px of nothing between the header and the fields. */
.record__edit { margin-top: 18px; }
.record__pair { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.record__pair:last-child { margin-bottom: 0; }

/* The prototype's gaps banner: a warning card with an icon tile and Fix it. */
.record .record__gaps {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.record__gaps-icon {
  display: grid;
  place-items: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: var(--xl-color-warning-container);
  color: var(--xl-color-warning);
}

.record__gaps-text {
  flex: 1;
  min-width: 200px;
  margin: 0;
  font-size: 13.5px;
  color: var(--xl-color-secondary-text);
}

/* Two columns while the page has room for them, one on anything narrower — the
   right column is reference material and reads fine underneath. The page's own
   width decides, not the window's: the sidebar collapses on its own schedule. */
.record__columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

/* No gap: each XlCard already carries the 14px under it. */
.record__col { display: flex; flex-direction: column; }

@container (max-width: 700px) {
  .record__columns { grid-template-columns: minmax(0, 1fr); }
}

.record__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.record__card-head h2 { margin: 0 10px 0 0; font-size: 15px; line-height: normal; font-weight: 700; }

/* A heading with no button beside it sits in the card's own 22px line. */
.record__card-head--plain { min-height: 22px; }

/* The prototype's own input on this page: 11px corners, a 14px inset and a
   12.5px label. */
.record :deep(.xl-field__label) { font-size: 12.5px; line-height: normal; }
.record :deep(.xl-field__control) { padding-left: 14px; padding-right: 14px; border-radius: 11px; }
.record :deep(.xl-tooltip--underline) { text-decoration-color: var(--xl-color-outline-variant); text-decoration-thickness: 1px; }

.record__card-actions { display: flex; gap: 10px; }

/* One card per policy, the way the prototype draws them: a renewal inside a
   month turns the border and the date red. */
.record__policy {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 14px 16px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: #fff;
}

.record__policy-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 36px;
  flex: 0 0 42px;
  border-radius: 13px;
}

.record__policy-what { flex: 1; min-width: 150px; }
.record__policy-what > b { font-size: 14.5px; }
.record__policy-what .record__muted { font-size: 12.5px; }

.record__policy-premium { margin-top: 3px; font-size: 12.5px; font-variant-numeric: tabular-nums; }
.record__policy-per { color: var(--xl-color-outline); }

/* Hugs the right of the row where it fits, and sits under the policy as one
   readable line where it does not. */
.record__policy-when { margin-left: auto; text-align: right; }
.record__policy-when b { font-size: 13.5px; }
.record__policy-when--due { color: var(--xl-color-error); }
/* The gap is a margin: Vue condenses the whitespace between the two elements
   away, so a space in the template never reaches the page. */
.record__policy-when .record__muted { margin-left: 5px; font-size: var(--xl-type-body-small-size); white-space: nowrap; }

.record__policy-total {
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px solid var(--xl-color-border-subtle);
}

.record__policy-total b { display: block; font-size: 14px; }

.record__policy-draft {
  margin-top: 10px;
  padding: 14px 16px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: #fff;
}

/* After both card rules, so a renewal inside a month reddens either one. */
.record__policy--due { border-color: #F1CBCB; }

.record__policy-fields { display: flex; gap: 9px; flex-wrap: wrap; align-items: flex-end; }
.record__policy-fields :deep(.xl-field__control) { height: 38px; }
.record__policy-fields :deep(.xl-dt__control) { height: 38px !important; border-radius: 11px !important; }
.record__policy-fields :deep(.xl-dt__label) {
  font-size: var(--xl-type-label-large-size);
  line-height: var(--xl-type-label-large-line);
}
/* The prototype's three lines at any card width: line and number, then premium
   and renewal date sharing theirs 136:158, then the remove button alone. The
   number and the button each claim the rest of their line, which is what
   forces the break after them. */
.record__policy-fields > :nth-child(1) { width: 126px; }
.record__policy-fields > :nth-child(2) { flex: 1 0 calc(100% - 135px); }
.record__policy-fields > :nth-child(3) { flex: 136 1 136px; }
.record__policy-fields > :nth-child(4) { flex: 158 1 158px; }

/* A figure reads against the decimal point, not the left edge. XlField hands
   its class to the input itself, not to its wrapper. */
.record__policy-fields :deep(input.record__premium) { text-align: right; }

.record__policy-description { margin-top: 9px; }

.record__policy-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  margin-right: calc(100% - 38px);
  padding: 0;
  border: 1px solid var(--xl-color-border);
  border-radius: 11px;
  background: transparent;
  color: var(--xl-color-error);
  cursor: pointer;
}

.record__policy-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 9px 0 0;
  font-size: 12.5px;
  color: var(--xl-color-warning);
}

.record__policy-hint svg { width: 12px; height: 12px; flex: 0 0 12px; }
.record__policy-hint--error { color: var(--xl-color-error); }

.record__muted { font-size: var(--xl-type-body-medium-size); color: var(--xl-color-outline); }

.record__none { margin: 8px 0 0; font-size: 13.5px; }

.record__warn { margin: 0; font-size: var(--xl-type-body-medium-size); color: var(--xl-color-error); }

.record__nobody {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 13px 15px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-secondary-text);
}

.record .record__nobody { margin-bottom: 0; background: #FCEBEB; box-shadow: none; }

.record__nobody svg {
  flex: 0 0 15px;
  width: 15px;
  height: 15px;
  margin-top: 2px;
  color: var(--xl-color-error);
}

.record__nobody p { margin: 0; }
.record__nobody b { color: var(--xl-color-primary-text); }

.record__broker-edit { margin-top: 14px; }
.record__broker-edit :deep(.xl-field) { margin-bottom: 8px; }

.record__broker-note {
  margin: 0;
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.record__broker-note--alert { color: var(--xl-color-error); }
.record__broker-note svg { width: 12px; height: 12px; vertical-align: -1px; }

.record__designation {
  display: grid;
  gap: 8px;
  justify-items: start;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--xl-color-border-subtle);
}

.record__designation label {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-secondary-text);
  cursor: pointer;
}

.record__designation-error {
  margin: 0;
  font-size: var(--xl-type-body-small-size);
  color: var(--xl-color-error);
}

.record__footnote {
  margin: 12px 0 0;
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.record__footnote a { color: var(--xl-color-primary); text-decoration: none; }
.record__footnote a:hover { text-decoration: underline; }

.record__broker { display: flex; align-items: center; gap: 14px; padding: 12px 0; }

/* Named, not `span`: XlAvatar's own root is a span, and a bare descendant
   selector turned it into a flex column, which dropped its initials out of the
   centre of the circle. */
.record__broker-who { flex: 1; min-width: 0; }
.record__broker-who b { display: block; font-size: 15px; }

/* A fixed label column, so the values in Account and Engagement start on the
   same line. */
.record__facts {
  display: grid;
  grid-template-columns: 118px 1fr;
  column-gap: 10px;
  margin: 10px 0 14px;
  font-size: 13.5px;
  line-height: normal;
}

.record__facts--last { margin-bottom: 0; }

.record__facts dt, .record__facts dd { padding: 8px 0; }
.record__facts dt { color: var(--xl-color-outline); }
.record__facts dd { margin: 0; font-weight: 500; }
.record__facts .record__facts-missing { color: var(--xl-color-error); }

.record__wide { width: 100%; justify-content: center; }
.record__revoke { margin-top: 9px; box-shadow: var(--xl-shadow-1); }

/* The prototype's answer rows: the question small and muted, the answer under it. */
.record__answer {
  padding: 12px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
  line-height: normal;
}

.record__answer-q { font-size: 12.5px; color: var(--xl-color-outline); }
.record__answer-a { margin-top: 3px; font-size: 14.5px; }

.record__answers-edit { display: flex; flex-direction: column; gap: 16px; margin-top: 14px; }

/* A picked answer in the same box as the typed ones around it, its menu
   dropping straight under it at the field's own width. */
.record__answer-pick, .record__line-pick, .record__broker-pick { display: flex; width: 100%; }
.record__answer-pick :deep(.xl-dropdown__label),
.record__line-pick :deep(.xl-dropdown__label) { font-size: 12.5px; line-height: normal; }
.record__answer-pick :deep(.xl-dropdown__control),
.record__line-pick :deep(.xl-dropdown__control),
.record__broker-pick :deep(.xl-dropdown__control) { width: 100%; min-width: 0; padding: 0 14px; border-radius: 11px; }
.record__answer-pick :deep(.xl-dropdown__menu),
.record__broker-pick :deep(.xl-dropdown__menu) { width: 100%; max-width: none; }
.record__line-pick :deep(.xl-dropdown__control) { height: 38px; }

/* Removing access is not undoable from here, so the button becomes the
   question rather than opening a dialog over a page built to avoid them. */
.record__confirm {
  margin-top: 10px;
  padding: 14px;
  border: 1px solid var(--xl-color-error);
  border-radius: var(--xl-radius-sm);
  background: var(--xl-color-error-container, #fdf3f3);
}

.record__confirm p {
  margin: 0 0 12px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-primary-text);
}

.record__confirm-actions { display: flex; gap: 8px; justify-content: flex-end; }
</style>
